import { For, type JSX, onCleanup, onMount, Show } from 'solid-js';
import { t } from '../i18n';
import { ZONE_NAME } from '../lib/fleet-live-arrange';
import { LIVE_ZONES, type LiveZone } from '../lib/fleet-live-layout';
import { FleetLiveIcon } from './FleetLiveIcon';
import { FleetLiveZoneIcon } from './FleetLiveZoneIcon';

// A panel's "Place in" menu (#6779, prototype openMenu, `:3638-3646`): Top, Middle, Side and
// Bottom, each with its zone map, the current one marked "Here" (`aria-pressed="true"`), then
// "Reset layout". It is the KEYBOARD path for moving a panel (the grip is pointer-only):
// focus moves to the first option that is not current, Esc closes it and returns focus to
// "Place", and a press outside the panel's controls closes it.

export type FleetLivePlaceMenuProps = {
  /** The panel's name, already translated. */
  readonly panel: string;
  readonly current: LiveZone;
  /** The element a press must land outside of to close the menu (the panel's controls). */
  readonly within: () => HTMLElement | undefined;
  readonly onPlace: (zone: LiveZone) => void;
  readonly onReset: () => void;
  /** Close; `refocus` when the keyboard closed it, so focus returns to "Place". */
  readonly onClose: (refocus: boolean) => void;
};

export function FleetLivePlaceMenu(props: FleetLivePlaceMenuProps): JSX.Element {
  let menu: HTMLFieldSetElement | undefined;
  onMount(() => {
    menu?.querySelector<HTMLButtonElement>('button[aria-pressed="false"]')?.focus();
    const outside = (e: PointerEvent): void => {
      const root = props.within();
      if (root !== undefined && e.target instanceof Node && root.contains(e.target)) return;
      props.onClose(false);
    };
    globalThis.document.addEventListener('pointerdown', outside);
    onCleanup(() => globalThis.document.removeEventListener('pointerdown', outside));
  });
  const onKeyDown = (e: KeyboardEvent): void => {
    if (e.key !== 'Escape') return;
    e.preventDefault();
    e.stopPropagation();
    props.onClose(true);
  };
  return (
    <fieldset
      ref={menu}
      class="fleet-live__menu"
      aria-label={t('fleet.live.place_panel', { panel: props.panel })}
      onKeyDown={onKeyDown}
    >
      <legend class="fleet-live__menu-head">{t('fleet.live.place_in')}</legend>
      <For each={LIVE_ZONES}>
        {(zone) => (
          <button
            type="button"
            class="fleet-live__menu-item"
            aria-pressed={zone === props.current}
            onClick={() => props.onPlace(zone)}
          >
            <FleetLiveZoneIcon zone={zone} />
            <span>{ZONE_NAME[zone]()}</span>
            <Show when={zone === props.current}>
              <i class="fleet-live__menu-here">{t('fleet.live.here')}</i>
            </Show>
          </button>
        )}
      </For>
      <button
        type="button"
        class="fleet-live__menu-item fleet-live__menu-reset"
        onClick={() => props.onReset()}
      >
        <FleetLiveIcon name="reset" />
        <span>{t('fleet.live.reset_layout')}</span>
      </button>
    </fieldset>
  );
}
